// Character display helpers shared by the mobile surfaces.
//
// These were local to MobileApp.tsx until the roster editor needed the same
// class colors, spec icons and role accents. Importing them back out of
// MobileApp would be a cycle (MobileApp renders the editor), so they live
// here and both sides import from one place. Everything is pure: given the
// game's field groups, produce a color, an icon URL or a label.

export interface DisplayOption {
  id: number;
  key: string;
  label: string;
  image_url?: string | null;
  metadata?: {
    color?: string;
    role_color?: string;
    class_key?: string;
    role_key?: string;
    allowed_class_keys?: string[];
  };
}

export interface DisplayFieldGroup {
  id: number;
  key: string;
  label: string;
  multiSelect: boolean;
  isRequired: boolean;
  options: DisplayOption[];
}

/** Fallback color generator (matches RosterPage). */
export function stringToColor(str: string): string {
  if (!str) return "#525252";
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const c = (hash & 0x00ffffff).toString(16).toUpperCase();
  return "#" + "00000".substring(0, 6 - c.length) + c;
}

/** Class color from the game's field definitions, falling back to a hash. */
export function getClassColorFromFields(className: string, fields: DisplayFieldGroup[]): string {
  const classGroup = fields.find(g => g.key === "class");
  if (classGroup) {
    const classOpt = classGroup.options.find(o => o.label.toLowerCase() === className.toLowerCase());
    if (classOpt?.metadata?.color) return classOpt.metadata.color;
  }
  return stringToColor(className);
}

/** Spec icon from fields (matches PlannerPage: spec option by label + class). */
export function getSpecIconUrl(
  specLabel: string | null | undefined,
  className: string | null | undefined,
  fields: DisplayFieldGroup[],
): string | null {
  if (!specLabel) return null;
  const specGroup = fields.find(g => g.key === "spec");
  if (!specGroup) return null;
  const classKey = (className || "").toLowerCase().replace(/\s+/g, "");
  const match =
    specGroup.options.find(o => o.label === specLabel && (
      o.metadata?.class_key === classKey ||
      (Array.isArray(o.metadata?.allowed_class_keys) && o.metadata!.allowed_class_keys!.includes(classKey))
    )) || specGroup.options.find(o => o.label === specLabel);
  return match?.image_url || null;
}

/** Class icon from fields (fallback when a spec icon isn't available). */
export function getClassIconUrl(className: string | null | undefined, fields: DisplayFieldGroup[]): string | null {
  if (!className) return null;
  const classGroup = fields.find(g => g.key === "class");
  const opt = classGroup?.options.find(o => o.label.toLowerCase() === className.toLowerCase());
  return opt?.image_url || null;
}

/**
 * Pick the spec the player is actually slotted as: match the assigned role
 * against the character's spec/role lists, falling back to their primary spec.
 */
export function resolveAssignedSpec(a: any): string {
  const meta = a.character_metadata || {};
  const assignedRole: string = a.role_label || "";
  const specLabels: string[] = meta.spec_labels || [];
  const roleLabels: string[] = meta.role_labels || [];
  if (assignedRole && roleLabels.length === specLabels.length && specLabels.length > 0) {
    const idx = roleLabels.findIndex(r => r === assignedRole);
    if (idx >= 0 && specLabels[idx]) return specLabels[idx];
  }
  return meta.primary_spec_label || specLabels[0] || assignedRole || meta.class_name || "";
}

/** Role accent color: sky/emerald/violet/orange so roles read at a glance. */
export function roleAccentColor(roleLabel: string | null | undefined): string {
  const r = (roleLabel || "").toLowerCase();
  if (r.includes("tank")) return "#38bdf8";
  if (r.includes("heal")) return "#34d399";
  if (r.includes("ranged")) return "#a78bfa";
  return "#fb923c"; // melee / generic dps
}

/** Compact role tag text. */
export function shortRole(roleLabel: string | null | undefined): string {
  const r = (roleLabel || "").toLowerCase();
  if (r.includes("tank")) return "Tank";
  if (r.includes("heal")) return "Heal";
  if (r.includes("ranged")) return "Ranged";
  if (r.includes("melee")) return "Melee";
  return "DPS";
}

/** Warcraft Logs parse color (matches RosterPage). */
export function getParseColor(score: number): string {
  if (score === 100) return "#e5cc80";
  if (score >= 99) return "#e268a8";
  if (score >= 95) return "#ff8000";
  if (score >= 75) return "#a335ee";
  if (score >= 50) return "#0070dd";
  if (score >= 25) return "#1eff00";
  return "#9d9d9d";
}
